import {
  Avatar,
  Button,
  Checkbox,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import AddAlarmIcon from '@material-ui/icons/AddAlarm';
import Skeleton from '@material-ui/lab/Skeleton';
import moment from 'moment';
import { MUIDataTableColumn, MUIDataTableMeta } from 'mui-datatables';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmationDialog from '../../../../../components/ConfirmationDialog/index';
import CustomDataTable from '../../../../../components/CustomDataTable';
import CustomDialog, {
  CustomDialogProps,
} from '../../../../../components/CustomDialog/index';
import SelectPeriod from '../../../../../components/SelectPeriod/index';
import TableSkeleton from '../../../../../components/TableSkeleton';
import { useLoadingDialog } from '../../../../../hooks/backdrop';
import { useFeedback } from '../../../../../hooks/feedback';
import { EmployeeDTO } from '../../../../../models/dto/EmployeeDTO';
import {
  DefaultWorkShift,
  EmployeePunchCardDTO,
  EmployeePunchCardSignDTO,
  OccurrencesControl,
} from '../../../../../models/dto/EmployeePunchCardDTO';
import { MedicalCertificateDTO } from '../../../../../models/dto/MedicalCertificateDTO';
import { PeriodDTO } from '../../../../../models/dto/PeriodDTO';
import { PeriodOccurrencesEnum } from '../../../../../models/enum/PeriodOccurrencesEnum';
import { api } from '../../../../../services/api';
import { AppState } from '../../../../../store';
import {
  getEmployeePunchCard,
  selectEmployee,
} from '../../../../../store/actions/employeesActions';
import { CondominiumReducerData } from '../../../../../store/reducers/condominiumReducer';
import { EmployeesReducerData } from '../../../../../store/reducers/employeesReducer';
import { formatFirstAndLastName } from '../../../../../utils/formatFirstAndLastName';
import { isNullOrEmpty } from '../../../../../utils/validation.util';
import AddRegistersDialog from '../AddRegistersDialog';
import { FormattedBatchFormsData } from '../BatchSigningPunchCard/BatchSigningPunchCardDialog';
import DownloadMedicalStatementMenu from './DownloadMedicalStatementMenu';
import EmployeePunchCardDetailsPopover from './EmployeePunchCardDetailsPopover';
import EmployeePunchCardObservationsDialog from './EmployeePunchCardObservationsDialog';

export interface AllowanceDiscount {
  tipoControleOcorrencia: string;
  dataEfetivo: string;
  numeroCadastro?: string;
  nomeFuncionario?: string;
  insereRefeicao?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
    chip: {
      margin: '5px 0 5px 10px',
    },
    dialogContainer: {
      overflowY: 'hidden',
    },
    selectMenu: {
      '&&:focus': {
        backgroundColor: 'transparent',
      },
    },
  }),
);

const EmployeePunchCardDialog: React.FC<CustomDialogProps> = ({
  open,
  handleClose,
  handleConfirm,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { addFeedback } = useFeedback();
  const { handleLoadingDialog } = useLoadingDialog();
  const [periodSelected, setPeriodSelected] = useState<PeriodDTO | null>(null);
  const [openObservationsDialog, setOpenObservationsDialog] = useState(false);
  const [openAddRegistersModal, setOpenAddRegistersModal] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [openDetailsPopover, setOpenDetailsPopover] = useState(false);
  const [openSignConfirmationDialog, setOpenSignConfirmationDialog] = useState(
    false,
  );
  const [rowSelected, setRowSelected] = useState<EmployeePunchCardDTO | null>(
    null,
  );
  const [registerDateSelected, setRegisterDateSelected] = useState<string>('');
  const [registersSelected, setRegistersSelected] = useState<string[]>([]);
  const [loadingPunchCard, setLoadingPunchCard] = useState<boolean>(false);
  const [loadingPunchCardSign, setLoadingPunchCardSign] = useState<boolean>(
    false,
  );
  const [employeePunchCardSign, setEmployeePunchCardSign] = useState<
    EmployeePunchCardSignDTO[]
  >([]);
  const [changedOccurrences, setChangedOccurrences] = useState<
    AllowanceDiscount[]
  >([]);
  const [initialEmployeePunchCard, setInitialEmployeePunchCard] = useState<
    EmployeePunchCardDTO[]
  >([]);
  const [employeePunchCard, setEmployeePunchCard] = useState<
    EmployeePunchCardDTO[]
  >([]);

  const { employeeSelected }: EmployeesReducerData = useSelector(
    (state: AppState) => state.employeesReducer,
  );

  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const getPunchCard = async (employee: EmployeeDTO, period: PeriodDTO) => {
    try {
      if (employeeSelected) setLoadingPunchCard(true);
      const data = await getEmployeePunchCard(employee, period);
      const sortedData = data.sort((a, b) => (a.data < b.data ? 1 : -1));
      setInitialEmployeePunchCard(sortedData);
      setEmployeePunchCard(sortedData);
    } catch (error) {
      console.log(error);
      setEmployeePunchCard([]);
      setInitialEmployeePunchCard([]);
    } finally {
      setLoadingPunchCard(false);
    }
  };

  const getEmployeePunchCardSign = async (
    employee: EmployeeDTO,
    period: PeriodDTO,
  ) => {
    try {
      setLoadingPunchCardSign(true);
      const { data } = await api.get<EmployeePunchCardSignDTO[]>(
        `/gdp/assinaturas`,
        {
          params: {
            numeroCadastro: employee.numeroCadastro,
            dataPeriodo: moment(period.mesFechamento, 'DD/MM/YYYY').format(
              'YYYY-MM-DD',
            ),
          },
        },
      );
      setEmployeePunchCardSign(data);
    } catch (error) {
      console.log(error);
      setEmployeePunchCardSign([]);
    } finally {
      setLoadingPunchCardSign(false);
    }
  };

  const handleChangeAllowanceDiscount = (
    event: ChangeEvent<{ name?: string | undefined; value: unknown }>,
    row: string[],
  ) => {
    event.stopPropagation();
    const registerDate = row[0];
    const valueSelected = String(event.target.value);
    const registerChanged = initialEmployeePunchCard.find(
      initial => initial.data === registerDate,
    );

    if (registerChanged) {
      if (valueSelected === 'ABONO' || valueSelected === 'PADRAO') {
        setChangedOccurrences([
          ...changedOccurrences,
          { tipoControleOcorrencia: valueSelected, dataEfetivo: registerDate },
        ]);
      } else if (valueSelected === 'DESCONTO') {
        setChangedOccurrences([
          ...changedOccurrences.filter(
            changedOccurrence => changedOccurrence.dataEfetivo !== registerDate,
          ),
        ]);
      }

      setEmployeePunchCard(
        [
          ...employeePunchCard.filter(
            punchCard => punchCard.data !== registerDate,
          ),
          {
            ...registerChanged,
            controleOcorrencia: {
              ...registerChanged.controleOcorrencia,
              tipoControleOcorrencia: valueSelected,
            },
          },
        ].sort((a, b) => (a.data < b.data ? 1 : -1)),
      );
    }
  };

  const handleAddRegisterButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any[],
  ) => {
    event.stopPropagation();
    setRegistersSelected(row[1]);
    setRegisterDateSelected(row[0]);
    setOpenAddRegistersModal(true);
  };

  const handleRowClick = (dataIndex: number) => {
    setRowSelected(employeePunchCard[dataIndex]);
    setOpenDetailsPopover(true);
  };

  const fillRegistersFormData = useCallback((): FormattedBatchFormsData => {
    const occurrencesControl: OccurrencesControl[] = [];
    const defaultWorkShifts: DefaultWorkShift[] = [];
    if (employeeSelected && condominiumSelected) {
      changedOccurrences.forEach(item => {
        if (item.tipoControleOcorrencia === 'PADRAO') {
          defaultWorkShifts.push({
            numeroCadastro: employeeSelected.numeroCadastro,
            justificativa: '',
            horarioPadrao: true,
            referencia: String(condominiumSelected.referencia),
            insereRefeicao: item.insereRefeicao ? 'sim' : 'nao',
            periodoDeHorarioPadrao: moment(item.dataEfetivo).format(
              'DD/MM/YYYY',
            ),
            periodoAteHorarioPadrao: moment(item.dataEfetivo).format(
              'DD/MM/YYYY',
            ),
          });
        } else {
          occurrencesControl.push({
            dataEfetivo: item.dataEfetivo,
            numeroCadastro: String(employeeSelected.numeroCadastro),
            minutos: 0,
            referencia: condominiumSelected.referencia,
            tipoControleOcorrencia: item.tipoControleOcorrencia,
          });
        }
      });
    }
    return { occurrencesControl, defaultWorkShifts };
  }, [changedOccurrences, employeeSelected, condominiumSelected]);

  const handleCheckLunchTime = (checked: boolean, punchCardDate: string) => {
    const registerChanged = employeePunchCard.find(
      initial => initial.data === punchCardDate,
    );
    if (registerChanged) {
      setEmployeePunchCard(
        [
          ...employeePunchCard.filter(
            punchCard => punchCard.data !== punchCardDate,
          ),
          {
            ...registerChanged,
            ...{ insereRefeicao: checked },
          },
        ].sort((a, b) => (a.data < b.data ? 1 : -1)),
      );

      const index = changedOccurrences.findIndex(
        changedOccurrence => changedOccurrence.dataEfetivo === punchCardDate,
      );
      changedOccurrences[index].insereRefeicao = checked;

      setChangedOccurrences(changedOccurrences);
    }
  };

  const handleSubmit = async () => {
    setOpenConfirmationDialog(false);
    handleLoadingDialog(true);
    try {
      const registersFormData: FormattedBatchFormsData = fillRegistersFormData();

      if (!isNullOrEmpty(registersFormData.occurrencesControl)) {
        await api.post(
          '/gdp/controle-ocorrencias',
          registersFormData.occurrencesControl,
        );
      }

      if (!isNullOrEmpty(registersFormData.defaultWorkShifts)) {
        await api.post('gdp/pontos/lote', registersFormData.defaultWorkShifts);
      }
      handleClose();
      dispatch(selectEmployee(null));
      handleLoadingDialog(false);
      addFeedback({
        type: 'success',
        closeTimeout: 7000,
        description: 'Alterações realizadas com sucesso!',
        onClose: () =>
          addFeedback({
            type: 'info',
            description:
              'O processamento dessa informação não é instantâneo, portanto levará algum tempo até que a informação seja exibida nas marcações do funcionário.',
            onClose: () => handleClose(),
          }),
      });
    } catch (error) {
      console.log(error);
      addFeedback({
        description: 'Ocorreu um erro ao realizar a operação',
        type: 'error',
      });
    } finally {
      handleLoadingDialog(false);
    }
  };

  const handleSignPunchCardMirror = async () => {
    setOpenSignConfirmationDialog(false);
    handleLoadingDialog(true);

    try {
      await api.put(`/gdp/assinaturas/${employeePunchCardSign[0]?.id}`, {
        flagAprovado: true,
      });
      addFeedback({
        type: 'success',
        description: 'Espelho de ponto aprovado com sucesso!',
      });
    } catch (error) {
      console.log(error);
      addFeedback({
        type: 'error',
        description: 'Ocorreu um erro ao aprovar o espelho de ponto!',
      });
    } finally {
      handleLoadingDialog(false);
      if (employeeSelected && periodSelected) {
        getEmployeePunchCardSign(employeeSelected, periodSelected);
      }
    }
  };

  useEffect(() => {
    if (employeeSelected && periodSelected) {
      getPunchCard(employeeSelected, periodSelected);
      getEmployeePunchCardSign(employeeSelected, periodSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeSelected, periodSelected]);

  const DialogActionsComponent = (
    <Grid container alignItems="center" justify="space-between">
      <Grid item>
        <Button
          variant="contained"
          color="primary"
          disabled={
            employeePunchCardSign.length === 0 ||
            employeePunchCardSign[0].flagAprovado
          }
          onClick={() => setOpenSignConfirmationDialog(true)}
        >
          Assinar espelho de ponto
        </Button>
      </Grid>

      <Grid item>
        <Button onClick={handleClose} variant="outlined" color="default">
          Cancelar
        </Button>
        <Button
          variant="contained"
          style={{ marginLeft: theme.spacing(1) }}
          disabled={changedOccurrences.length === 0 || loadingPunchCard}
          color="primary"
          type="button"
          onClick={() => setOpenConfirmationDialog(true)}
        >
          Salvar
        </Button>
      </Grid>
    </Grid>
  );

  const columns: MUIDataTableColumn[] = [
    {
      name: 'data',
      label: 'Data',
      options: {
        filter: false,
        customBodyRender: (value: string) =>
          moment(value).format('ddd - DD/MM/YYYY'),
      },
    },
    {
      name: 'horarios',
      label: 'Marcações',
      options: {
        filter: false,
        sort: false,
        setCellProps: () => {
          return {
            style: {
              minWidth: '240px',
              maxWidth: '270px',
            },
          };
        },
        customBodyRender: (value: string[]) => value.join(' - '),
      },
    },

    {
      name: 'ocorrencias',
      label: 'Ocorrências',
      options: {
        sort: false,
        setCellProps: () => {
          return {
            style: {
              minWidth: '240px',
              maxWidth: '270px',
            },
          };
        },
        customBodyRender: (value: string[] | null) =>
          value?.filter(ocorrencia => ocorrencia !== 'No Horario').join(' - '),
      },
    },
    {
      name: 'controleOcorrencia',
      label: 'Abonar / Descontar / Horário Padrão',
      options: {
        print: false,
        searchable: false,
        sort: false,
        filter: false,
        download: false,
        hint:
          'Alterações são permitidas apenas em casos de FALTA, ATRASOS e/ou SAÍDA ANTECIPADA.',
        setCellProps: () => {
          return {
            style: {
              maxWidth: '190px',
            },
          };
        },
        customBodyRender: (
          value: OccurrencesControl | null,
          tableMeta: MUIDataTableMeta,
        ) => {
          return (
            <Select
              classes={{
                selectMenu: classes.selectMenu,
              }}
              defaultValue={value?.tipoControleOcorrencia || 'NADA'}
              disableUnderline
              disabled={
                !tableMeta.rowData[2]?.find(
                  (item: any) =>
                    item ===
                      PeriodOccurrencesEnum.FALTA_SEM_JUSTIFICATIVA
                        .description ||
                    item ===
                      PeriodOccurrencesEnum.ATRASOS_SEM_JUSTIFICATIVA
                        .description ||
                    item ===
                      PeriodOccurrencesEnum.SAIDA_ANTECIPADA_SEM_JUSTIFICATIVA
                        .description,
                ) || !!value?.dataCriado
              }
              fullWidth
              value={value?.tipoControleOcorrencia || 'DESCONTO'}
              onChange={event => {
                handleChangeAllowanceDiscount(event, tableMeta.rowData);
              }}
              onClose={event => event.stopPropagation()}
            >
              <MenuItem value="ABONO">Abonar</MenuItem>
              <MenuItem value="DESCONTO">Descontar</MenuItem>
              <MenuItem value="PADRAO">Inserir horário Padrão</MenuItem>
            </Select>
          );
        },
      },
    },
    {
      name: 'insereRefeicao',
      label: 'Inserir Horário de Almoço',
      options: {
        hint:
          'Marcando esta opção o sistema irá considerar também, o horário de almoço do funcionário para inserção automática do horário padrão.',
        sort: false,
        customBodyRender: (
          value: boolean | undefined,
          tableMeta: MUIDataTableMeta,
        ) => {
          const punchCardData = tableMeta.rowData[0];
          return (
            <Checkbox
              color="primary"
              checked={!!value}
              disabled={
                !employeePunchCard.find(
                  item =>
                    item.data === punchCardData &&
                    item?.controleOcorrencia?.tipoControleOcorrencia ===
                      'PADRAO',
                )
              }
              onChange={event => {
                event.stopPropagation();
                handleCheckLunchTime(
                  event.currentTarget.checked,
                  punchCardData,
                );
              }}
              onClick={event => event.stopPropagation()}
            />
          );
        },
      },
    },
    {
      name: 'horarios',
      label: 'Adicionar Marcações',
      options: {
        searchable: false,
        filter: false,
        sort: false,
        print: false,
        download: false,
        setCellHeaderProps: () => {
          return {
            style: {
              textAlign: 'center',
            },
          };
        },
        setCellProps: () => {
          return {
            style: {
              textAlign: 'center',
            },
          };
        },
        customBodyRender: (value: string[], tableMeta: MUIDataTableMeta) => {
          return (
            <Tooltip arrow title="Adicionar Marcação">
              <IconButton
                color="primary"
                onClick={event =>
                  handleAddRegisterButtonClick(event, tableMeta.rowData)
                }
                disabled={value.length >= 6}
              >
                <AddAlarmIcon />
              </IconButton>
            </Tooltip>
          );
        },
      },
    },
    {
      name: 'atestados',
      label: 'Atestados',
      options: {
        searchable: false,
        sort: false,
        print: false,
        download: false,
        setCellHeaderProps: () => {
          return {
            style: {
              textAlign: 'center',
            },
          };
        },
        setCellProps: () => {
          return {
            style: {
              textAlign: 'center',
            },
          };
        },
        customBodyRender: (value: MedicalCertificateDTO[]) => (
          <DownloadMedicalStatementMenu options={value} />
        ),
      },
    },
  ];

  return (
    <>
      {openAddRegistersModal && (
        <AddRegistersDialog
          open={openAddRegistersModal}
          registersSelected={registersSelected}
          registerDateSelected={registerDateSelected}
          handleCloseModal={() => setOpenAddRegistersModal(false)}
        />
      )}

      {openDetailsPopover && rowSelected && (
        <EmployeePunchCardDetailsPopover
          open={openDetailsPopover}
          employeeRegisters={rowSelected}
          employeeData={employeeSelected}
          handleClose={() => setOpenDetailsPopover(false)}
        />
      )}

      <CustomDialog
        open={open}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        maxWidth="lg"
        showCloseButton
        contentDividers
        title="Gestão de horas trabalhadas"
        containerStyle={classes.dialogContainer}
        CustomDialogActionsComponent={DialogActionsComponent}
      >
        <Grid container spacing={0} alignItems="center" justify="space-between">
          <Grid>
            <SelectPeriod
              showLabel
              disableUnderline
              value={periodSelected}
              handlePeriodChange={setPeriodSelected}
            />
          </Grid>
          <Grid>
            <Typography
              style={{ display: 'inline', marginLeft: 16 }}
              color="textPrimary"
              variant="body1"
            >
              Status da Assinatura do Funcionário:
            </Typography>
            <Typography
              style={{
                display: 'inline',
                fontWeight:
                  employeePunchCardSign.length === 0 ? 'bold' : 'normal',
              }}
              color={
                employeePunchCardSign.length === 0 ? 'primary' : 'textPrimary'
              }
              variant="body1"
            >
              {loadingPunchCardSign ? (
                <Skeleton
                  style={{ display: 'inline-block', paddingTop: 5 }}
                  width={100}
                  height={30}
                />
              ) : (
                <>
                  {employeePunchCardSign.length === 0
                    ? '  Não assinado'
                    : employeePunchCardSign[0].flagAprovado
                    ? '  Aprovado'
                    : '  Assinado'}
                </>
              )}
            </Typography>
          </Grid>
          <Grid>
            <Button
              onClick={() => setOpenObservationsDialog(true)}
              variant="outlined"
              color="default"
            >
              Observações
            </Button>
          </Grid>
        </Grid>

        <List className={classes.listContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <ListItem dense disableGutters>
                <ListItemAvatar>
                  <Avatar />
                </ListItemAvatar>
                <ListItemText
                  primary={formatFirstAndLastName(employeeSelected?.nome || '')}
                  primaryTypographyProps={{ style: { fontWeight: 'bold' } }}
                  secondary={employeeSelected?.cargo?.descricao}
                />
              </ListItem>
            </Grid>
            <Grid item xs={12} md={3}>
              <ListItem dense disableGutters>
                <ListItemText
                  primary="Horário"
                  primaryTypographyProps={{ style: { fontWeight: 'bold' } }}
                  secondary={employeeSelected?.escala?.descricaoJornada}
                />
              </ListItem>
            </Grid>
            <Grid item xs={12} md={2}>
              <ListItem dense disableGutters>
                <ListItemText
                  primary="Admissão"
                  primaryTypographyProps={{ style: { fontWeight: 'bold' } }}
                  secondary={moment(
                    employeeSelected?.dataAdmissao || '',
                    'YYYY-MM-DD',
                  ).format('DD/MM/YYYY')}
                />
              </ListItem>
            </Grid>
            <Grid item xs={12} md={2}>
              <ListItem dense disableGutters>
                <ListItemText
                  primary="Matrícula"
                  primaryTypographyProps={{ style: { fontWeight: 'bold' } }}
                  secondary={employeeSelected?.numeroCadastro || ''}
                />
              </ListItem>
            </Grid>
            <Grid item xs={12} md={2}>
              <ListItem dense disableGutters>
                <ListItemText
                  primary="PIS"
                  primaryTypographyProps={{ style: { fontWeight: 'bold' } }}
                  secondary={employeeSelected?.pis}
                />
              </ListItem>
            </Grid>
          </Grid>
        </List>

        {loadingPunchCard ? (
          <TableSkeleton rows={3} />
        ) : (
          <CustomDataTable
            data={employeePunchCard}
            columns={columns}
            options={{
              elevation: 0,
              pagination: false,
              download: true,
              print: true,
              downloadOptions: {
                filename: `Folha de Ponto ${employeeSelected?.nome}`,
              },
              onRowClick: (_rowData, { dataIndex }) =>
                handleRowClick(dataIndex),
            }}
            title="Folha de Ponto"
          />
        )}
      </CustomDialog>
      {openObservationsDialog && periodSelected && employeeSelected && (
        <EmployeePunchCardObservationsDialog
          open={openObservationsDialog}
          period={periodSelected}
          employeeData={employeeSelected}
          handleClose={() => setOpenObservationsDialog(false)}
        />
      )}
      <ConfirmationDialog
        open={openConfirmationDialog}
        title="Atenção!"
        mainText="Após concluir, esta ação não poderá ser revertida. Deseja continuar?"
        onConfirm={handleSubmit}
        onCancel={() => setOpenConfirmationDialog(false)}
        handleClose={() => setOpenConfirmationDialog(false)}
      />
      <ConfirmationDialog
        open={openSignConfirmationDialog}
        title="Atenção!"
        mainText="Tem certeza que deseja assinar o espelho de ponto para este período?"
        onConfirm={handleSignPunchCardMirror}
        onCancel={() => setOpenSignConfirmationDialog(false)}
        handleClose={() => setOpenSignConfirmationDialog(false)}
      />
    </>
  );
};

export default EmployeePunchCardDialog;
